import { useRef, useState, type ChangeEvent } from "react";
import { Button, Card, CardContent, CardHeader, CardTitle, FormTextarea } from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { centsToUsd } from "@/lib/money";
import { useImportExpenses } from "@/api/hooks/useExpenses";
import { EXPENSE_CATEGORY_LABELS, type ExpenseCategory } from "@/api/types/expense";

/**
 * Bank-import area for expenses. Two independent entry points that both
 * POST the same {csv_text} shape (never parsed client-side — the API owns
 * CSV parsing, categorization, and dedup per the contract):
 *  1. A file input read locally via FileReader, auto-imported on selection.
 *  2. A paste-CSV textarea fallback, submitted explicitly (for machines/
 *     browsers where picking a real file isn't convenient, and for tests).
 */
export function ImportExpensesPanel() {
  const importExpenses = useImportExpenses();
  const [pasteText, setPasteText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function runImport(csvText: string) {
    const trimmed = csvText.trim();
    if (!trimmed) {
      toastError("Nothing to import", "Paste or choose a CSV file first.");
      return;
    }
    try {
      await importExpenses.mutateAsync({ csv_text: trimmed });
      toastSuccess("Import complete");
    } catch (err) {
      toastError("Couldn't import CSV", getErrorMessage(err));
    }
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      void runImport(text);
    };
    reader.onerror = () => {
      toastError("Couldn't read file", "The CSV file could not be read from disk.");
    };
    reader.readAsText(file);

    // Reset so selecting the same file again re-triggers onChange.
    e.target.value = "";
  }

  const result = importExpenses.data;

  return (
    <Card data-testid="expense-import-panel">
      <CardHeader>
        <CardTitle>Import expenses from CSV</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary" htmlFor="expense-import-file">
            Upload CSV file
          </label>
          <input
            ref={fileInputRef}
            id="expense-import-file"
            type="file"
            accept=".csv,text/csv"
            aria-label="Upload CSV file"
            data-testid="expense-import-file-input"
            onChange={handleFileChange}
            disabled={importExpenses.isPending}
            className="block w-full text-sm text-text-secondary file:mr-3 file:rounded-md file:border file:border-border file:bg-bg-card file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-bg-hover"
          />
        </div>

        <div className="flex items-center gap-3 text-xs text-text-muted">
          <div className="h-px flex-1 bg-border" />
          or paste CSV rows
          <div className="h-px flex-1 bg-border" />
        </div>

        <FormTextarea
          label="Paste CSV"
          placeholder={"Date,Description,Amount\n2026-06-01,Costco - bar supplies,-125.50"}
          rows={5}
          value={pasteText}
          data-testid="expense-import-paste-textarea"
          onChange={(e) => setPasteText(e.target.value)}
        />
        <div className="flex justify-end">
          <Button
            type="button"
            variant="secondary"
            disabled={importExpenses.isPending || !pasteText.trim()}
            onClick={() => void runImport(pasteText)}
          >
            {importExpenses.isPending ? "Importing..." : "Import pasted CSV"}
          </Button>
        </div>

        {result && (
          <div
            className="rounded-lg border border-border bg-bg-hover/50 p-4"
            data-testid="expense-import-result"
          >
            <p className="text-sm font-medium text-text-primary">
              Imported{" "}
              <span data-testid="expense-import-imported-count">{result.imported_count}</span>,
              skipped{" "}
              <span data-testid="expense-import-skipped-count">{result.skipped_count}</span> of{" "}
              {result.row_count} rows
            </p>
            {Object.keys(result.by_category).length > 0 && (
              <ul className="mt-2 flex flex-col gap-1 text-xs text-text-secondary">
                {Object.entries(result.by_category).map(([category, cents]) => (
                  <li key={category} className="flex items-center justify-between">
                    <span>{EXPENSE_CATEGORY_LABELS[category as ExpenseCategory] ?? category}</span>
                    <span className="font-medium text-text-primary">{centsToUsd(cents)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
